import React, { useState } from 'react';

import { useDispatch } from 'react-redux';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import { updateManyApplicationStatusSubscription } from '../application-status-subscription/actions/update-many-application-status-subscription';

import { StatusChangeChips } from '../job/settings/status-change-chips';
import { StatusChangeNotificationSelect } from '../job/settings/status-change-notification-select';
import { UiButton } from '../ui/ui-button';

import { AddCustomEmailButton } from '../application-status-subscription/widgets/add-custom-email-button';
import { SORT_ORDER, sortAlphabetically } from '../util/sort-functions';

type InputProps = {
  org: Org;
  users: Array<User>;
  subscriptionMap: {
    users: { [username: string]: Array<ApplicationStatus> };
    customEmails: { [email: string]: Array<ApplicationStatus> };
  };
};

export const ApplicantStatusSubscriptionsTable = (props: InputProps) => {
  const { org, users, subscriptionMap } = props;

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [usernameSubscriptions, setUsernameSubscriptions] = useState<{
    [username: string]: Array<ApplicationStatus>;
  }>(subscriptionMap.users);
  const [emailSubscriptions, setEmailSubscriptions] = useState<{
    [email: string]: Array<ApplicationStatus>;
  }>(subscriptionMap.customEmails);

  const dispatch = useDispatch();
  const handleSave = () => {
    dispatch(
      updateManyApplicationStatusSubscription({
        orgId: org.org_id,
        usernameSubscriptions,
        emailSubscriptions,
      }),
    );
    setIsEditing(false);
  };

  const handleIsEditing = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setUsernameSubscriptions(subscriptionMap.users);
    setEmailSubscriptions(subscriptionMap.customEmails);
  };

  const handleSelectStatusSubscription =
    ({ username, email }: { username?: string; email?: string }) =>
    (event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
      const addedStatus = event.target.value as ApplicationStatus;

      if (username) {
        setUsernameSubscriptions({
          ...usernameSubscriptions,
          [username]: (usernameSubscriptions[username] || []).concat([addedStatus]),
        });
      } else if (email) {
        setEmailSubscriptions({
          ...emailSubscriptions,
          [email]: (emailSubscriptions[email] || []).concat([addedStatus]),
        });
      }
    };

  const handleRemoveStatusSubscription =
    ({ username, email }: { username?: string; email?: string }) =>
    (removedStatus: ApplicationStatus) =>
    () => {
      if (username) {
        setUsernameSubscriptions({
          ...usernameSubscriptions,
          [username]: (usernameSubscriptions[username] || []).filter(
            (status) => status !== removedStatus,
          ),
        });
      } else if (email) {
        setEmailSubscriptions({
          ...emailSubscriptions,
          [email]: (emailSubscriptions[email] || []).filter((status) => status !== removedStatus),
        });
      }
    };

  const handleAddCustomEmailSubscription = (email: string) => {
    setEmailSubscriptions({
      ...emailSubscriptions,
      [email]: [],
    });
  };

  return (
    <div className="u-maxWidth-800px u-padding-top-2x">
      <div className="u-display-flex">
        <div className="u-margin-right-2x">
          <div id="week-in-review-subscription" className="typo-h5 typo-bold">
            Applicant Status Change Notifications
          </div>
          <div className="u-margin-bottom-xl">Receive an email when applicants move statuses.</div>
        </div>
        {isEditing ? (
          <div>
            <UiButton variant="tertiary" className="u-margin-right-md" onClick={handleCancel}>
              Cancel
            </UiButton>
            <UiButton variant="primary" onClick={handleSave}>
              Save
            </UiButton>
          </div>
        ) : (
          <div>
            <UiButton variant="primary" onClick={handleIsEditing}>
              Edit
            </UiButton>
          </div>
        )}
      </div>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>User</TableCell>
            <TableCell align="center">Subscribed Statuses</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users
            .filter((u) => u.isActive)
            .sort(sortAlphabetically((u) => u.firstName, SORT_ORDER.ASC))
            .map((u) => (
              <TableRow key={`app-status-subs-${u.username}`}>
                <TableCell>
                  {u.firstName} {u.lastName}
                </TableCell>
                <TableCell align="center">
                  {isEditing ? (
                    <StatusChangeNotificationSelect
                      emailOrUsername={u.username}
                      subscribedStatuses={usernameSubscriptions[u.username]}
                      usesPhoneInterview={org.usesPhoneInterview}
                      onAdd={handleSelectStatusSubscription({ username: u.username })}
                      onRemove={handleRemoveStatusSubscription({ username: u.username })}
                    />
                  ) : (
                    <StatusChangeChips
                      keyPrefix={u.username}
                      selectedStatuses={usernameSubscriptions[u.username]}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          {Object.keys(emailSubscriptions).map((otherEmail) => (
            <TableRow key={`app-status-subs-${otherEmail}`}>
              <TableCell>{otherEmail}</TableCell>
              <TableCell align="center">
                {isEditing ? (
                  <StatusChangeNotificationSelect
                    emailOrUsername={otherEmail}
                    subscribedStatuses={emailSubscriptions[otherEmail]}
                    usesPhoneInterview={org.usesPhoneInterview}
                    onAdd={handleSelectStatusSubscription({ email: otherEmail })}
                    onRemove={handleRemoveStatusSubscription({ email: otherEmail })}
                  />
                ) : (
                  <StatusChangeChips
                    keyPrefix={otherEmail}
                    selectedStatuses={emailSubscriptions[otherEmail]}
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={2}>
              <AddCustomEmailButton onSubmit={handleAddCustomEmailSubscription} />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};
